import { createContext, useContext, useState } from "react"
import { getCreativeAsyncAnalyzeJobStatus, getManyCreativesById } from '@/services/api.js'
import { toast } from 'sonner'
import { useDashboardContext } from '@/context-state/DashboardContext.jsx'
import { handleError } from '@/services/errorHandler.js'

export const CreativeAnalyticsContext = createContext(null)

export function CreativeAnalyticsProvider({ children }) {

    const { creativeContext } = useDashboardContext()
    const [adGroupReportsByKey, setAdGroupReportsByKey] = useState({}) // The 'key' will be the `${adAccountId}_${startDate}_${endDate}`

    const [superSegmentsDataByKey, setSuperSegmentsDataByKey] = useState({}) // The 'key' will be the `${adAccountId}_${startDate}_${endDate}`
    const [superSegmentsReportsByKey, setSuperSegmentsReportsByKey] = useState({}) // The 'key' will be the `${adAccountId}_${startDate}_${endDate}`

    const [customAdGroupReportsByKey, setCustomAdGroupReportsByKey] = useState({}) // The 'key' will be the `${adAccountId}_${startDate}_${endDate}`
    const [adsByKey, setAdsByKey] = useState({}) // This is a flat array of all the ads data for the chosen dates by the same key
    const [insightsByKey, setInsightsByKey] = useState({}) // Insights per creatives set by the same key (Ex. "creative-id_creative-id2")
    const [isPollingAnalyzeProcessStatus, setIsPollingAnalyzeProcessStatus] = useState(false)
    const { updateCreativeState } = creativeContext
    const [videoExplorerDataByKey, setVideoExplorerDataByKey] = useState({}) // The 'key' will be the `${adAccountId}_${startDate}_${endDate}`
    const [superSegmentsSuggestionsByKey, setSuperSegmentsSuggestionsByKey] = useState({}) // The 'key' will be the creative_ids.join('_')

    const getInsightsByKey = (key) => insightsByKey[key]

    const updateInsightsByKey = (key, insightsData) => {
        setInsightsByKey({ ...insightsByKey, [key]: insightsData })
    }

    const getReportByGroupType = (key, groupType) => {
        return adGroupReportsByKey[key]?.[groupType] || []
    }

    const updateReportByGroupType = (key, report) => {
        setAdGroupReportsByKey({ ...adGroupReportsByKey, [key]: report })
    }

    const getCustomReportByGroupId = (key, groupId) => {
        return customAdGroupReportsByKey[key]?.[groupId]
    }

    const updateCustomReportByGroupIds = (key, groupsData) => {
        const newState = { ...customAdGroupReportsByKey }
        for (const group of groupsData) {
            if (!newState[key]) {
                newState[key] = {}
            }
            newState[key][group.id] = group
        }
        setCustomAdGroupReportsByKey(newState)
    }

    const getSuperSegmentsDataByKey = (key, query) => {
        return superSegmentsDataByKey[key]?.[query]
    }

    const updateSuperSegmentsData = (key, superSegmentsData) => {
        const newState = { ...superSegmentsDataByKey }
        for (const superSegment of superSegmentsData) {
            if (!newState[key]) {
                newState[key] = {}
            }
            newState[key][superSegment.query] = superSegment
        }
        setSuperSegmentsDataByKey(newState)
    }

    const getSuperSegmentsReportsByKey = (key, query) => {
        return superSegmentsReportsByKey[key]?.[query]
    }

    const updateSuperSegmentsReports = (key, superSegmentsReports) => {
        const newState = { ...superSegmentsReportsByKey }
        for (const report of superSegmentsReports) {
            if (!newState[key]) {
                newState[key] = {}
            }
            newState[key][report.id] = report
        }
        setSuperSegmentsReportsByKey(newState)
    }

    const getAdsByKey = (key) => {
        return adsByKey[key]
    }

    const updateAdsByKey = (key, adsData) => {
        setAdsByKey({ ...adsByKey, [key]: adsData })
    }

    // Process of the async ads analyze
    const triggerAnalyzeProcessPolling = async (creativeIds) => {
        try {
            if (isPollingAnalyzeProcessStatus) {
                return
            }
            for (let i = 0; i < 10; i++) {
                const isRunningResponse = await getCreativeAsyncAnalyzeJobStatus()
                if (isRunningResponse.data) {
                    setIsPollingAnalyzeProcessStatus(true)
                    pollAsyncProcessStatus(creativeIds)
                    return
                }
                await new Promise(resolve => setTimeout(resolve, 2000))
            }
        } catch (e) {
            handleError(e)
            console.log(e)
        }
    }

    const pollAsyncProcessStatus = async (creativeIds) => {
        try {
            for (let i = 0; i < 500; i++) {
                const isStillRunningResponse = await getCreativeAsyncAnalyzeJobStatus()
                if (!isStillRunningResponse.data) {
                    setIsPollingAnalyzeProcessStatus(false)
                    const creativesResponse = await getManyCreativesById(creativeIds)
                    updateCreativeState(creativesResponse.data)
                    toast.success('Done analyzing your creatives')
                    return
                }
                await new Promise(resolve => setTimeout(resolve, 2000))
            }
        } catch (e) {
            handleError(e)
            console.log(e)
        }
    }


    return (
        <CreativeAnalyticsContext.Provider
            value={{
                triggerAnalyzeProcessPolling,
                isPollingAnalyzeProcessStatus,
                superSegmentsDataByKey,
                getSuperSegmentsDataByKey,
                updateSuperSegmentsData,
                superSegmentsReportsByKey,
                getSuperSegmentsReportsByKey,
                updateSuperSegmentsReports,
                adGroupReportsByKey,
                getReportByGroupType,
                getCustomReportByGroupId,
                updateCustomReportByGroupIds,
                getAdsByKey,
                updateAdsByKey,
                videoExplorerDataByKey,
                setVideoExplorerDataByKey,
                updateReportByGroupType,
                adsByKey,
                getInsightsByKey,
                updateInsightsByKey,
                superSegmentsSuggestionsByKey,
                setSuperSegmentsSuggestionsByKey
            }}>
            {children}
        </CreativeAnalyticsContext.Provider>
    )
}

export const useCreativeAnalyticsContext = () => {
    const context = useContext(CreativeAnalyticsContext)

    if (!context) throw new Error('useCreativeAnalyticsContext context must be use inside CreativeAnalyticsProvider');

    return context;
}